import { useCallback, useEffect, useRef, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { NetworkStatus } from '@apollo/client'

import { getCannedCameraFeedStreamState } from '@/features/canned-video'
import { useCameraFeedQuery } from '@/graphql/generated/hooks'
import { CameraFeedQuery } from '@/graphql/generated/operations'

import { CameraStream, VideoFeedEvent } from '../types/types'
import { CameraStreamStateIProps, IncidentPlayerError } from '../types/types'

const TIMEOUT_IN_MS = 30_000
const MINUTE_IN_MS = 60 * 1000

type LiveKitStreamingUrlWrapper = Extract<
  NonNullable<CameraFeedQuery['streamingUrlWrapper']>,
  { __typename?: 'StreamingUrlWrapperLiveKit' }
>

const isLiveKitWrapper = (
  wrapper: CameraFeedQuery['streamingUrlWrapper']
): wrapper is LiveKitStreamingUrlWrapper =>
  wrapper?.__typename === 'StreamingUrlWrapperLiveKit'

export const useCameraFeed = (
  cameraId?: string,
  onEvent?: (event: VideoFeedEvent) => void
): CameraStreamStateIProps => {
  const timeoutRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [url, setUrl] = useState(null)
  const [stream, setStream] = useState<CameraStream | null>(null)
  const queryTimeoutRef = useRef(null)

  const isCannedVideoEnabled = useFeatureFlag(FeatureFlag.cannedVideo)

  const processData = (data: CameraFeedQuery) => {
    onEvent?.(VideoFeedEvent.Received)
    if (isLiveKitWrapper(data.streamingUrlWrapper)) {
      // The hook instance survives camera switches, so an HLS camera may have
      // left behind a url and a pending auto-refresh; both must go before the
      // LiveKit session starts, or url-driven consumers keep playing the
      // previous camera and the timer keeps re-fetching.
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
      setUrl(null)
      const { url: apiUrl, token } = data.streamingUrlWrapper
      if (apiUrl && token) {
        setStream({ kind: 'livekit', apiUrl, token })
        setError(null)
        // No refresh timer: a LiveKit grant is one-shot (3-minute start
        // window, 10 concurrent streams per Lumana org), so re-fetching on a
        // cadence would mint new streams. livekit-client owns reconnection
        // after the initial connect.
      } else {
        // An empty url/token can't produce a playable stream — surface an
        // error rather than hand the player an unconnectable grant.
        setStream(null)
        setError(IncidentPlayerError.ERROR_FETCHING_STREAM)
      }
      return
    }
    const { url, expiryTime } = data.streamingUrlWrapper
    if (url && expiryTime) {
      setUrl(url)
      setStream({ kind: 'hls', url })
      setError(null)
      timeoutRef.current = setTimeout(safeRefetch, MINUTE_IN_MS)
    } else {
      // An HLS wrapper missing its url or expiry can't be played, and the hook
      // instance survives camera switches — so leaving the previous camera's
      // state in place would keep a LiveKit grant alive and play camera A's
      // footage in camera B's drawer. Match handleCompleted's no-wrapper
      // branch and clear everything.
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
      setUrl(null)
      setStream(null)
      setError(IncidentPlayerError.NO_VIDEO_FEED)
    }
  }

  const handleCompleted = (data: CameraFeedQuery) => {
    // Clear the query timeout since we received a response
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current)
      queryTimeoutRef.current = null
    }

    setIsLoading(false)
    if (data.streamingUrlWrapper) {
      processData(data)
    } else {
      setUrl(null)
      setStream(null)
      if (isCannedVideoEnabled) {
        return
      } else {
        setError(IncidentPlayerError.NO_VIDEO_FEED)
      }
    }
  }

  const handleQueryTimeout = () => {
    setIsLoading(false)
    setUrl(null)
    setStream(null)
    setError(IncidentPlayerError.ERROR_FETCHING_STREAM)
    queryTimeoutRef.current = null
  }

  const handleError = () => {
    // Clear the query timeout since we received an error response
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current)
      queryTimeoutRef.current = null
    }
    setIsLoading(false)
    setUrl(null)
    setStream(null)
    setError(IncidentPlayerError.ERROR_FETCHING_STREAM)
  }

  const { refetch, networkStatus } = useCameraFeedQuery({
    variables: { deviceId: cameraId },
    fetchPolicy: 'no-cache',
    skip: !cameraId || isCannedVideoEnabled,
    onCompleted: handleCompleted,
    onError: handleError,
  })

  // Apollo's refetch rejects on failure; that rejection already surfaces via the
  // query's onError handler, so swallow it here to avoid an unhandled promise
  // rejection when it's fired without a .catch — both from the internal
  // auto-refresh timeout and from callers like a retry button.
  const safeRefetch = useCallback(() => {
    refetch().catch(() => undefined)
  }, [refetch])

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
      if (queryTimeoutRef.current) {
        clearTimeout(queryTimeoutRef.current)
      }
    }
  }, [])

  const armQueryTimeout = () => {
    if (!queryTimeoutRef.current) {
      queryTimeoutRef.current = setTimeout(handleQueryTimeout, TIMEOUT_IN_MS)
    }
  }

  useEffect(() => {
    switch (networkStatus) {
      case NetworkStatus.setVariables:
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
        clearTimeout(queryTimeoutRef.current)
        queryTimeoutRef.current = null
        setUrl(null)
        setStream(null)
        setError(null)
        setIsLoading(true)
        onEvent?.(VideoFeedEvent.BeginRequesting)
        armQueryTimeout()
        break
      case NetworkStatus.loading:
        armQueryTimeout()
        break
    }
  }, [networkStatus])

  return isCannedVideoEnabled
    ? getCannedCameraFeedStreamState(cameraId)
    : { url, isLoading, errorMessage: error, refetch: safeRefetch, stream }
}
